"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { PutUpdateUserBody } from "@/api/pizza-checker/schemas";
import { useBranch } from "@/context/branch-context";
import { useRole } from "@/context/role-context";
import { User } from "@/context/user-context";
import { toast } from "sonner";
import { useCreateUser, useUpdateUser } from "./use-user-mutations";
import {
  ALL_BRANCHES_VALUE,
  fromBranchSelectValue,
  toBranchSelectValue,
} from "./user-utils";
import { useLocalization } from "@/context/localization-context";

interface UserFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The user to edit; without one the form creates a new user. */
  user?: User | null;
}

type FormData = {
  login: string;
  psw: string;
  first_name: string;
  last_name: string;
  role_id: string;
  /** Value of the branch select; empty until a branch (or "All branches") is picked. */
  branch: string;
};

const emptyForm: FormData = {
  login: "",
  psw: "",
  first_name: "",
  last_name: "",
  role_id: "",
  branch: "",
};

const formFromUser = (user: User): FormData => ({
  login: user.login,
  psw: "",
  first_name: user.first_name ?? "",
  last_name: user.last_name ?? "",
  role_id: user.role_id,
  branch: toBranchSelectValue(user.branch_id),
});

export function UserForm({ open, onOpenChange, user }: UserFormProps) {
  const { t } = useLocalization();
  const { branches } = useBranch();
  const { getAvailableRoles, getRoleName } = useRole();
  const { createUser, isCreating } = useCreateUser();
  const { updateUser, isUpdating } = useUpdateUser();
  const [formData, setFormData] = useState<FormData>(emptyForm);

  // Start from the user's current data (or a blank form) every time the dialog opens
  React.useEffect(() => {
    if (open) {
      setFormData(user ? formFromUser(user) : emptyForm);
    }
  }, [open, user]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const login = formData.login.trim();
    const first_name = formData.first_name.trim();
    const last_name = formData.last_name.trim();

    if (!login || !formData.role_id || !formData.branch || (!user && !formData.psw)) {
      toast.error(t("users.form.requiredFields"));
      return;
    }

    const branch_id = fromBranchSelectValue(formData.branch);

    if (!user) {
      const created = await createUser(
        { login, psw: formData.psw, first_name, last_name, role_id: formData.role_id, branch_id },
        { success: t("users.form.created"), error: t("users.form.createFailed") },
      );
      if (created) onOpenChange(false);
      return;
    }

    // Send only what changed
    const changes: PutUpdateUserBody = {};
    if (login !== user.login) changes.login = login;
    if (formData.role_id !== user.role_id) changes.role_id = formData.role_id;
    if (branch_id !== user.branch_id) changes.branch_id = branch_id;
    if (first_name !== (user.first_name ?? "")) changes.first_name = first_name;
    if (last_name !== (user.last_name ?? "")) changes.last_name = last_name;

    if (Object.keys(changes).length === 0) {
      onOpenChange(false);
      return;
    }

    const updated = await updateUser(user._id, changes, {
      success: t("users.form.updated"),
      error: t("users.form.updateFailed"),
    });
    if (updated) onOpenChange(false);
  };

  const isLoading = isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {user ? t("users.form.editTitle") : t("users.form.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {user
              ? t("users.form.editDescription")
              : t("users.form.createDescription")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="login">{t("users.fields.login")} *</Label>
              <Input
                id="login"
                value={formData.login}
                onChange={(e) => handleInputChange("login", e.target.value)}
                placeholder="user@example.com"
                autoComplete="off"
                disabled={isLoading}
                required
              />
            </div>

            {!user && (
              <div>
                <Label htmlFor="password">{t("users.fields.password")} *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.psw}
                  onChange={(e) => handleInputChange("psw", e.target.value)}
                  placeholder={t("users.form.passwordPlaceholder")}
                  autoComplete="new-password"
                  disabled={isLoading}
                  required
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="first_name">{t("users.fields.firstName")}</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange("first_name", e.target.value)}
                  placeholder={t("users.fields.firstName")}
                  disabled={isLoading}
                />
              </div>
              <div>
                <Label htmlFor="last_name">{t("users.fields.lastName")}</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange("last_name", e.target.value)}
                  placeholder={t("users.fields.lastName")}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="role">{t("users.fields.role")} *</Label>
              <Select
                value={formData.role_id}
                onValueChange={(value) => handleInputChange("role_id", value)}
                disabled={isLoading}
              >
                <SelectTrigger id="role">
                  <SelectValue placeholder={t("users.fields.selectRole")}>
                    {formData.role_id ? getRoleName(formData.role_id) : undefined}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {getAvailableRoles().map((role) => (
                    <SelectItem key={role._id} value={role._id}>
                      {role.name || role.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="branch">{t("common.branch")} *</Label>
              <Select
                value={formData.branch}
                onValueChange={(value) => handleInputChange("branch", value)}
                disabled={isLoading}
              >
                <SelectTrigger id="branch">
                  <SelectValue placeholder={t("users.fields.selectBranch")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_BRANCHES_VALUE}>{t("users.fields.allBranches")}</SelectItem>
                  {branches?.filter(branch => branch.bb_branch_id).map((branch) => (
                    <SelectItem key={branch.bb_branch_id} value={branch.bb_branch_id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? t("users.form.saving")
                : user
                  ? t("users.form.saveChanges")
                  : t("users.form.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
